import { z } from 'zod';

import { useTypedSearchParams } from './use-typed-search-params';

/**
 * Zod schema for Signals page URL query parameters
 * Uses .catch() for graceful degradation to defaults
 */
const viewTypeSchema = z.enum(['list', 'chart', 'matrix'] as const).catch('list');

const sortBySchema = z
  .enum(['feedback', 'changes'] as const)
  .catch('feedback');

const sortOrderSchema = z.enum(['asc', 'desc'] as const).catch('desc');

const topCountSchema = z.coerce.number().int().min(5).max(100).catch(5);

const favoriteSchema = z
  .enum(['true', 'false'] as const)
  .catch('false')
  .transform((v) => v === 'true');

const showNewSchema = z
  .enum(['true', 'false'] as const)
  .catch('true')
  .transform((v) => v === 'true');

export const signalsSearchParamsSchema = z.object({
  view: viewTypeSchema,
  favorite: favoriteSchema,
  showNew: showNewSchema,
  sortBy: sortBySchema,
  sortOrder: sortOrderSchema,
  topCount: topCountSchema,
});

export type SignalsSearchParams = z.infer<typeof signalsSearchParamsSchema>;

/**
 * Convenience hook for Signals page search params
 * Provides type-safe access to URL query parameters
 *
 * @example
 * ```tsx
 * const [params, setParams] = useSignalsSearchParams();
 * console.log(params.view); // 'list' | 'chart' | 'matrix'
 * setParams({ view: 'chart', topCount: 10 });
 * ```
 */
export function useSignalsSearchParams() {
  return useTypedSearchParams(signalsSearchParamsSchema);
}
